import React, { useEffect, useMemo, useState } from "react";
import { authAPI, coursesAPI, examsAPI } from "../services/api";
import { firebaseAuth } from "../firebase";
import "../styles/Profile.css";
import {
  FaUserCircle,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaChalkboardTeacher,
  FaUserGraduate,
  FaClipboardList,
  FaEdit,
  FaSave,
  FaChartLine,
  FaBook,
  FaListUl,
  FaLock,
  FaBell,
  FaSignOutAlt,
  FaCheckCircle,
  FaCalendarAlt,
  FaTimes,
} from "react-icons/fa";

const Profile = () => {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({ courses: 0, students: 0, exams: 0 });
  const [activeTab, setActiveTab] = useState("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ name: "", phone: "", bio: "", location: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await authAPI.syncUser();
        const userData = userRes?.data?.user || userRes?.data || null;
        setUser(userData);
        setFormData({
          name: userData?.full_name || userData?.name || "",
          phone: userData?.phone || "",
          bio: userData?.bio || "",
          location: userData?.location || "",
        });

        const coursesRes = await coursesAPI.getAll();
        const coursesList = Array.isArray(coursesRes?.data) ? coursesRes.data : (coursesRes?.data?.data || []);

        let totalStudents = 0;
        let totalExams = 0;

        for (const course of coursesList) {
          try {
            const enrollmentRes = await coursesAPI.getEnrollments(course.id, 'approved');
            const enrollments = Array.isArray(enrollmentRes?.data) ? enrollmentRes.data : [];
            totalStudents += enrollments.length;
          } catch (e) { /* skip */ }

          try {
            const examsRes = await examsAPI.getByCourse(course.id);
            const exams = Array.isArray(examsRes?.data) ? examsRes.data : [];
            totalExams += exams.length;
          } catch (e) { /* skip */ }
        }

        setStats({ courses: coursesList.length, students: totalStudents, exams: totalExams });
      } catch (error) {
        console.error("Error loading profile:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    if (!formData.name.trim()) { alert("الاسم مطلوب."); return; }
    try {
      setSaving(true);
      if (typeof authAPI.updateProfile === "function") {
        const res = await authAPI.updateProfile(formData);
        const updated = res?.data?.user || res?.data || null;
        if (updated) setUser(updated);
        else setUser((prev) => ({ ...prev, ...formData }));
      } else {
        setUser((prev) => ({ ...prev, ...formData }));
      }
      setIsEditing(false);
    } catch (e) {
      alert("فشل حفظ التعديلات. حاول مرة أخرى.");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    if (!window.confirm("هل تريد تسجيل الخروج؟")) return;
    try {
      await firebaseAuth.signOut();
      window.location.href = "/login";
    } catch (e) {
      alert("فشل تسجيل الخروج.");
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.currentPassword) { alert("الرجاء إدخال كلمة المرور الحالية"); return; }
    if (!passwordForm.newPassword || passwordForm.newPassword.length < 6) { alert("كلمة المرور يجب أن تكون 6 أحرف على الأقل"); return; }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) { alert("كلمة المرور الجديدة وتأكيدها غير متطابقين"); return; }
    try {
      setChangingPassword(true);
      await firebaseAuth.reauthenticate(passwordForm.currentPassword);
      await firebaseAuth.updatePassword(passwordForm.newPassword);
      alert("✅ تم تغيير كلمة المرور بنجاح!");
      setShowPasswordModal(false);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      if (error.code === "auth/wrong-password") alert("❌ كلمة المرور الحالية غير صحيحة");
      else alert("❌ فشل تغيير كلمة المرور. حاول مرة أخرى.");
    } finally {
      setChangingPassword(false);
    }
  };

  const initials = useMemo(() => {
    const n = user?.full_name || user?.name || "م";
    return n.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  }, [user]);

  const joinDate = useMemo(() => {
    if (!user?.created_at) return "—";
    return new Date(user.created_at).toLocaleDateString("ar-EG", { year: "numeric", month: "long", day: "numeric" });
  }, [user]);

  if (loading) {
    return (
      <div className="pv2-page" dir="rtl">
        <div className="pv2-skeleton">
          <div className="pv2-sk-left" />
          <div className="pv2-sk-right">
            <div className="pv2-sk-bar" />
            <div className="pv2-sk-bar short" />
            <div className="pv2-sk-bar" />
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="pv2-page" dir="rtl">
        <div className="pv2-error-state">تعذر تحميل بيانات الملف الشخصي.</div>
      </div>
    );
  }

  return (
    <div className="pv2-page fade-in" dir="rtl">
      <div className="pv2-layout">

        {/* ===== LEFT PANEL: Profile Card ===== */}
        <aside className="pv2-aside">
          <div className="pv2-aside-cover" />

          <div className="pv2-avatar-wrap">
            <div className="pv2-avatar">
              {initials}
            </div>
          </div>

          <div className="pv2-aside-body">
            <h2 className="pv2-name">{user.full_name || user.name || "معلّم"}</h2>

            <div className="pv2-badges">
              <span className="pv2-role-badge">
                <FaChalkboardTeacher /> معلّم
              </span>
              <span className={`pv2-status-badge ${user.status === "active" ? "active" : "inactive"}`}>
                <FaCheckCircle />
                {user.status === "active" ? "نشط" : "غير نشط"}
              </span>
            </div>

            <p className="pv2-bio">
              {formData.bio?.trim() ? formData.bio : "لا توجد نبذة شخصية بعد."}
            </p>

            <div className="pv2-contact-list">
              <div className="pv2-contact-item">
                <FaEnvelope className="pv2-contact-icon" />
                <span className="pv2-contact-val">{user.email || "—"}</span>
              </div>
              {formData.phone?.trim() && (
                <div className="pv2-contact-item">
                  <FaPhone className="pv2-contact-icon" />
                  <span className="pv2-contact-val">{formData.phone}</span>
                </div>
              )}
              {formData.location?.trim() && (
                <div className="pv2-contact-item">
                  <FaMapMarkerAlt className="pv2-contact-icon" />
                  <span className="pv2-contact-val">{formData.location}</span>
                </div>
              )}
              <div className="pv2-contact-item">
                <FaCalendarAlt className="pv2-contact-icon" />
                <span className="pv2-contact-val">انضم {joinDate}</span>
              </div>
            </div>

            {/* Stats */}
            <div className="pv2-stats">
              <div className="pv2-stat-item blue">
                <span className="pv2-stat-num">{stats.courses}</span>
                <span className="pv2-stat-lbl">دورة</span>
              </div>
              <div className="pv2-stat-item green">
                <span className="pv2-stat-num">{stats.students}</span>
                <span className="pv2-stat-lbl">طالب</span>
              </div>
              <div className="pv2-stat-item purple">
                <span className="pv2-stat-num">{stats.exams}</span>
                <span className="pv2-stat-lbl">اختبار</span>
              </div>
            </div>
          </div>
        </aside>

        {/* ===== RIGHT PANEL: Tabbed Content ===== */}
        <main className="pv2-main">

          {/* Tabs + Edit button */}
          <div className="pv2-topbar">
            <div className="pv2-tabs">
              <button className={`pv2-tab ${activeTab === "overview" ? "active" : ""}`} onClick={() => setActiveTab("overview")}>
                <FaListUl /> نظرة عامة
              </button>
              <button className={`pv2-tab ${activeTab === "courses" ? "active" : ""}`} onClick={() => setActiveTab("courses")}>
                <FaBook /> دوراتي
              </button>
              <button className={`pv2-tab ${activeTab === "settings" ? "active" : ""}`} onClick={() => setActiveTab("settings")}>
                <FaLock /> إعدادات الحساب
              </button>
            </div>

            <button
              className={`pv2-btn-edit ${isEditing ? "saving" : ""}`}
              onClick={() => isEditing ? handleSave() : setIsEditing(true)}
              disabled={saving}
              type="button"
            >
              {isEditing ? (<><FaSave /> {saving ? "جارٍ الحفظ..." : "حفظ التغييرات"}</>) : (<><FaEdit /> تعديل الملف</>)}
            </button>
          </div>

          {/* ── Tab: Overview ── */}
          {activeTab === "overview" && (
            <div className="pv2-tab-content">
              <div className="pv2-section-title">📌 المعلومات الشخصية</div>

              <div className="pv2-fields-grid">
                {/* الاسم */}
                <div className="pv2-field">
                  <label className="pv2-field-label"><FaUserCircle /> الاسم الكامل</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pv2-input"
                      placeholder="الاسم الكامل"
                    />
                  ) : (
                    <div className="pv2-field-val">{user.full_name || user.name || "—"}</div>
                  )}
                </div>

                {/* الإيميل */}
                <div className="pv2-field">
                  <label className="pv2-field-label"><FaEnvelope /> البريد الإلكتروني</label>
                  <div className="pv2-field-val muted">{user.email || "—"}</div>
                </div>

                {/* الهاتف */}
                <div className="pv2-field">
                  <label className="pv2-field-label"><FaPhone /> رقم الهاتف</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="pv2-input"
                      placeholder="أضف رقم هاتفك"
                    />
                  ) : (
                    <div className="pv2-field-val">{formData.phone?.trim() ? formData.phone : "غير محدد"}</div>
                  )}
                </div>

                {/* الموقع */}
                <div className="pv2-field">
                  <label className="pv2-field-label"><FaMapMarkerAlt /> الموقع</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="pv2-input"
                      placeholder="المدينة / الدولة"
                    />
                  ) : (
                    <div className="pv2-field-val">{formData.location?.trim() ? formData.location : "غير محدد"}</div>
                  )}
                </div>

                {/* النبذة — full width */}
                <div className="pv2-field full-width">
                  <label className="pv2-field-label">📝 النبذة الشخصية</label>
                  {isEditing ? (
                    <textarea
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      className="pv2-input"
                      placeholder="اكتب نبذة قصيرة عنك..."
                      rows={4}
                    />
                  ) : (
                    <div className="pv2-field-val">{formData.bio?.trim() ? formData.bio : "—"}</div>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="pv2-save-row">
                  <button className="pv2-btn-primary" type="button" onClick={handleSave} disabled={saving}>
                    <FaSave /> {saving ? "جارٍ الحفظ..." : "حفظ"}
                  </button>
                  <button className="pv2-btn-ghost" type="button" onClick={() => setIsEditing(false)} disabled={saving}>
                    إلغاء
                  </button>
                </div>
              )}

              {/* Stats Cards Row */}
              <div className="pv2-section-title" style={{ marginTop: "2rem" }}>⚡ إحصائياتي</div>
              <div className="pv2-stat-cards">
                <div className="pv2-stat-card blue">
                  <div className="pv2-sc-icon"><FaChalkboardTeacher /></div>
                  <div className="pv2-sc-body">
                    <div className="pv2-sc-num">{stats.courses}</div>
                    <div className="pv2-sc-lbl">الدورات التدريبية</div>
                  </div>
                </div>
                <div className="pv2-stat-card green">
                  <div className="pv2-sc-icon"><FaUserGraduate /></div>
                  <div className="pv2-sc-body">
                    <div className="pv2-sc-num">{stats.students}</div>
                    <div className="pv2-sc-lbl">الطلاب المسجلين</div>
                  </div>
                </div>
                <div className="pv2-stat-card purple">
                  <div className="pv2-sc-icon"><FaClipboardList /></div>
                  <div className="pv2-sc-body">
                    <div className="pv2-sc-num">{stats.exams}</div>
                    <div className="pv2-sc-lbl">الاختبارات المنشأة</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Courses ── */}
          {activeTab === "courses" && (
            <div className="pv2-tab-content">
              <div className="pv2-section-title"><FaBook /> ملخص دوراتي</div>
              <div className="pv2-courses-summary">
                <div className="pv2-cs-row">
                  <span><FaChalkboardTeacher /> عدد الدورات</span>
                  <strong>{stats.courses}</strong>
                </div>
                <div className="pv2-cs-row">
                  <span><FaUserGraduate /> إجمالي الطلاب</span>
                  <strong>{stats.students}</strong>
                </div>
                <div className="pv2-cs-row">
                  <span><FaClipboardList /> إجمالي الاختبارات</span>
                  <strong>{stats.exams}</strong>
                </div>
              </div>
              <div className="pv2-courses-hint">
                لعرض تفاصيل كل دورة، اذهب إلى صفحة <a href="/courses">الدورات</a>.
              </div>
            </div>
          )}

          {/* ── Tab: Settings ── */}
          {activeTab === "settings" && (
            <div className="pv2-tab-content">
              <div className="pv2-section-title"><FaLock /> إعدادات الحساب</div>
              <div className="pv2-settings-list">
                <button className="pv2-setting-btn" type="button" onClick={() => setShowPasswordModal(true)}>
                  <div className="pv2-sb-icon lock"><FaLock /></div>
                  <div className="pv2-sb-text">
                    <span className="pv2-sb-title">تغيير كلمة المرور</span>
                    <span className="pv2-sb-desc">حدّث كلمة المرور لحساب أكثر أماناً</span>
                  </div>
                </button>
                <button className="pv2-setting-btn" type="button" onClick={() => alert("إعدادات الإشعارات قريباً.")}>
                  <div className="pv2-sb-icon bell"><FaBell /></div>
                  <div className="pv2-sb-text">
                    <span className="pv2-sb-title">إعدادات الإشعارات</span>
                    <span className="pv2-sb-desc">تحكم في طريقة استقبال الإشعارات</span>
                  </div>
                </button>
                <button className="pv2-setting-btn danger" type="button" onClick={handleLogout}>
                  <div className="pv2-sb-icon logout"><FaSignOutAlt /></div>
                  <div className="pv2-sb-text">
                    <span className="pv2-sb-title">تسجيل الخروج</span>
                    <span className="pv2-sb-desc">الخروج من حسابك الحالي</span>
                  </div>
                </button>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* ===== Password Modal ===== */}
      {showPasswordModal && (
        <div className="pv2-modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="pv2-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pv2-modal-header">
              <h3>🔐 تغيير كلمة المرور</h3>
              <button type="button" className="pv2-modal-close" onClick={() => setShowPasswordModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="pv2-modal-body">
              <div className="pv2-form-group">
                <label>كلمة المرور الحالية</label>
                <input type="password" value={passwordForm.currentPassword} onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} className="pv2-input" disabled={changingPassword} />
              </div>
              <div className="pv2-form-group">
                <label>كلمة المرور الجديدة</label>
                <input type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} className="pv2-input" placeholder="6 أحرف على الأقل" disabled={changingPassword} />
              </div>
              <div className="pv2-form-group">
                <label>تأكيد كلمة المرور الجديدة</label>
                <input type="password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} className="pv2-input" disabled={changingPassword} />
              </div>
            </div>
            <div className="pv2-modal-footer">
              <button className="pv2-btn-primary" onClick={handleChangePassword} disabled={changingPassword} type="button">
                {changingPassword ? "جارٍ التغيير..." : "تغيير كلمة المرور"}
              </button>
              <button className="pv2-btn-ghost" onClick={() => { setShowPasswordModal(false); setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" }); }} disabled={changingPassword} type="button">
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
